/**
 * Scope catalog — every authorization scope the platform registers, grouped per service.
 * Source of truth: each service's `*.Application/Security/Scopes.cs` in the platform repo.
 */

export const SECURITY = {
  apiKeyCreate: 'security.api-key.create',
  apiKeyRead: 'security.api-key.read',
  apiKeyReadAll: 'security.api-key.read-all',
  apiKeyUpdate: 'security.api-key.update',
  grantsCreate: 'security.grants.create',
  grantsDelete: 'security.grants.delete',
  grantsRead: 'security.grants.read',
  grantsReadAll: 'security.grants.read-all',
  membershipCreate: 'security.membership.create',
  membershipDelete: 'security.membership.delete',
  membershipRead: 'security.membership.read',
  roleCreate: 'security.role.create',
  roleRead: 'security.role.read',
  roleReadAll: 'security.role.read-all',
  roleUpdate: 'security.role.update',
  serviceCreate: 'security.service.create',
  serviceDelete: 'security.service.delete',
  serviceRead: 'security.service.read',
  serviceReadAll: 'security.service.read-all',
  serviceUpdate: 'security.service.update',
  summaryReadAll: 'security.summary.read-all',
  userCreate: 'security.user.create',
  userRead: 'security.user.read',
  userReadAll: 'security.user.read-all',
  userUpdate: 'security.user.update',
  workspaceOauthRead: 'security.workspace-oauth.read',
  workspaceOauthUpdate: 'security.workspace-oauth.update',
} as const

export const ACCOUNT = {
  accountCreate: 'account.account.create',
  invitationAccept: 'account.invitation.accept',
  invitationCreate: 'account.invitation.create',
  invitationDelete: 'account.invitation.delete',
  invitationRead: 'account.invitation.read',
  invitationUpdate: 'account.invitation.update',
} as const

export const WORKSPACE = {
  create: 'account.workspace.create',
  read: 'account.workspace.read',
  readAll: 'account.workspace.read-all',
  update: 'account.workspace.update',
  urlsCreate: 'account.workspace.urls.create',
  urlsDelete: 'account.workspace.urls.delete',
  urlsRead: 'account.workspace.urls.read',
  invitationAccept: 'account.workspace.invitation.accept',
  invitationCreate: 'account.workspace.invitation.create',
  invitationDelete: 'account.workspace.invitation.delete',
  invitationRead: 'account.workspace.invitation.read',
  invitationUpdate: 'account.workspace.invitation.update',
} as const

export const FABRIC = {
  serviceCreate: 'fabric.service.create',
  serviceDelete: 'fabric.service.delete',
  serviceRead: 'fabric.service.read',
  serviceReadAll: 'fabric.service.read-all',
  serviceShutdown: 'fabric.service.shutdown',
} as const

export const SETTINGS = {
  delete: 'fabric.setting.delete',
  read: 'fabric.setting.read',
  readAll: 'fabric.setting.read-all',
  update: 'fabric.setting.update',
} as const

export const NODE = {
  branchCreate: 'node.branch.create',
  branchRead: 'node.branch.read',
  branchReadAll: 'node.branch.read-all',
  branchUpdate: 'node.branch.update',
  contentCreate: 'node.content.create',
  contentRead: 'node.content.read',
  contentReadAll: 'node.content.read-all',
  contentUpdate: 'node.content.update',
  fileDelete: 'node.file.delete',
  fileRead: 'node.file.read',
  journalCreate: 'node.journal.create',
  journalRead: 'node.journal.read',
  journalReadAll: 'node.journal.read-all',
  journalUpdate: 'node.journal.update',
  nodeCreate: 'node.node.create',
  nodeReadAll: 'node.node.read-all',
  nodeUpdate: 'node.node.update',
} as const

export const EXECUTION = {
  run: 'execution.execution.run',
} as const

export const AI = {
  conversationConverse: 'ai.conversation.converse',
  conversationDelete: 'ai.conversation.delete',
  conversationRead: 'ai.conversation.read',
  toolExecute: 'ai.tool.execute',
  toolRead: 'ai.tool.read',
} as const

export const RESOURCE = {
  create: 'resources.resource.create',
  delete: 'resources.resource.delete',
  read: 'resources.resource.read',
  readAll: 'resources.resource.read-all',
  update: 'resources.resource.update',
} as const

export const MARKETPLACE = {
  billingManage: 'marketplace.billing.manage',
  entitlementCreate: 'marketplace.entitlement.create',
  entitlementRead: 'marketplace.entitlement.read',
  installCreate: 'marketplace.install.create',
  installDelete: 'marketplace.install.delete',
  installRead: 'marketplace.install.read',
  moderationReview: 'marketplace.moderation.review',
  packageCreate: 'marketplace.package.create',
  packageDelete: 'marketplace.package.delete',
  packagePublish: 'marketplace.package.publish',
  packageRead: 'marketplace.package.read',
  packageReadAll: 'marketplace.package.read-all',
  packageUpdate: 'marketplace.package.update',
  packageVisibilityManage: 'marketplace.package.visibility.manage',
  publisherCreate: 'marketplace.publisher.create',
  publisherRead: 'marketplace.publisher.read',
  publisherReadAll: 'marketplace.publisher.read-all',
  publisherUpdate: 'marketplace.publisher.update',
  reportCreate: 'marketplace.report.create',
  reportReview: 'marketplace.report.review',
  statsRead: 'marketplace.stats.read',
  trustManage: 'marketplace.trust.manage',
} as const

/** The standard VFS read/write set an engine run needs (mirrors the python engine manifest). */
export const ENGINE_DEFAULT_PERMISSIONS = [
  NODE.fileRead,
  NODE.contentRead,
  NODE.branchRead,
  NODE.nodeCreate,
  NODE.nodeUpdate,
  NODE.contentCreate,
  NODE.contentUpdate,
  NODE.journalCreate,
  NODE.journalUpdate,
  NODE.branchUpdate,
  NODE.fileDelete,
  EXECUTION.run,
] as const
